<template>
    <div style="width: 99%; display: block; padding: 5px; margin-left: auto; margin-right: auto;">
        <!-- Low Inventory PO Modal -->
        <b-modal id="create-low-inventory-po-modal" title="Create Low Inventory Purchase Order" size="xl" @hidden="resetLowInventoryPOForm" hide-footer>
            <CreateLowInventoryPO 
                :selectedItems="selectedItems"
                @po-created="handleLowInventoryPOCreated"
                @cancel="$bvModal.hide('create-low-inventory-po-modal')"
                ref="createLowInventoryPO"
            />
        </b-modal>

        <!-- Edit Product Modal -->
        <b-modal id="edit-item-modal" title="Edit Product" size="lg" hide-footer>
            <EditProductForm 
                :product="selectedProduct"
                @saved="handleProductSaved"
                @cancel="$bvModal.hide('edit-item-modal')"
            />
        </b-modal>

        <div v-if="items_being_loaded == true">
            <loading
                :active="true"
                :can-cancel="false"
                :is-full-page="true">
            </loading>
        </div>

        <div v-else>
            <!-- Filters Section -->
            <div
                v-if="isMobile"
                class="d-flex justify-content-between align-items-center mb-2 px-1"
            >
                <b-button
                    size="sm"
                    variant="outline-primary"
                    @click="filtersVisible = !filtersVisible"
                >
                    <b-icon-funnel-fill class="mr-1"></b-icon-funnel-fill>
                    {{ filtersVisible ? 'Hide Filters' : 'Show Filters' }}
                </b-button>
            </div>

            <b-collapse :visible="!isMobile || filtersVisible" class="mb-2">
                <b-card style="background: #f2f2f2; margin-bottom: 10px;">
                    <b-card-text>
                        <div style="display: flex; flex-wrap: wrap; gap: 10px;">
                            <div style="width: 200px;">
                                <div>FILTER VENDOR</div>
                                <b-form-select
                                    :options="filteredVendors"
                                    v-model="vendor_filter"
                                    @change="onVendorChange"
                                ></b-form-select>
                            </div>

                            <div style="width: 200px;">
                                <div>FILTER CATEGORY</div>
                                <b-form-select
                                    :options="category_options"
                                    v-model="category_filter"
                                ></b-form-select>
                            </div>

                            <div style="width: 300px;">
                                <div>SEARCH</div>
                                <b-form-input
                                    placeholder="SKU or Product Name or Vendor"
                                    v-model="search_filter"
                                ></b-form-input>
                            </div>

                            <div style="width: 200px;">
                                <div>LOCATION</div>
                                <b-form-select
                                    :options="location_options"
                                    v-model="location_filter"
                                    @change="onLocationChange"
                                ></b-form-select>
                            </div>

                            <div style="width: 200px;">
                                <div>INVENTORY LEVEL</div>
                                <b-form-select
                                    :options="inventory_level_options"
                                    v-model="inventory_level_filter"
                                ></b-form-select>
                            </div>

                            <div style="width: 200px;">
                                <div>STATUS</div>
                                <b-form-select
                                    :options="status_options"
                                    v-model="status_filter"
                                ></b-form-select>
                            </div>

                            <!-- New PO Status Filter -->
                            <div style="width: 200px;">
                                <div>PO STATUS</div>
                                <b-form-select
                                    :options="po_status_options"
                                    v-model="po_status_filter"
                                ></b-form-select>
                            </div>
                        </div>
                    </b-card-text>
                </b-card>
            </b-collapse>

            <!-- Action Buttons -->
            <div style="margin-bottom: 15px; display: flex; gap: 10px; flex-wrap: wrap;">
                <b-button 
                    variant="primary" 
                    @click="showCreateLowInventoryPOModal"
                    :disabled="selectableItemsCount === 0"
                >
                    Create Low Inventory PO ({{ selectableItemsCount }} selectable items)
                </b-button>
                
                <b-button 
                    variant="success" 
                    @click="exportToExcel"
                >
                    Export to Excel
                </b-button>
                
                <b-button 
                    variant="info" 
                    @click="refreshData"
                >
                    Refresh Data
                </b-button>
            </div>

            <!-- Summary Stats -->
            <b-card v-if="filtered.length > 0" style="margin-bottom: 10px; background: #e8f4fc;">
                <b-card-text>
                    <div style="display: flex; flex-wrap: wrap; gap: 20px;">
                        <div>
                            <strong>Total Items:</strong> {{ filtered.length }}
                        </div>
                        <div>
                            <strong>Critical Items (≤ 2):</strong> 
                            <span style="color: #dc3545;">{{ criticalCount }}</span>
                        </div>
                        <div>
                            <strong>Out of Stock:</strong> 
                            <span style="color: #dc3545;">{{ outOfStockCount }}</span>
                        </div>
                        <div>
                            <strong>Low Items (3-4):</strong> 
                            <span style="color: #ffc107;">{{ lowCount }}</span>
                        </div>
                        <div>
                            <strong>With Active PO:</strong> 
                            <span style="color: #dc3545;">{{ itemsWithActivePO.length }}</span>
                        </div>
                        <div>
                            <strong>Without Active PO:</strong> 
                            <span style="color: #28a745;">{{ itemsWithoutActivePO.length }}</span>
                        </div>
                        <div>
                            <strong>Total Value:</strong> ${{ totalValue.toFixed(2) }}
                        </div>
                    </div>
                </b-card-text>
            </b-card>

            <!-- Main Table -->
            <div v-if="filtered.length == 0">
                <b-alert show variant="success">NO LOW INVENTORY ITEMS FOUND</b-alert>
            </div>
            <div v-else class="diver">
                <table>
                    <thead>
                        <tr>
                            <th class="srr" style="width: 50px; background-color: lightyellow;">
                                <input
                                    type="checkbox"
                                    v-model="selectAll"
                                    @change="toggleSelectAll"
                                    :disabled="itemsWithoutActivePO.length === 0"
                                    :title="itemsWithoutActivePO.length === 0 ? 'No items without active POs' : ''"
                                >
                            </th>
                            <th class="srr" style="width: 150px; background-color: lightyellow;">PRODUCT SKU</th>
                            <th class="srr" style="width: 250px; background-color: lightyellow;">PRODUCT NAME</th>
                            <th class="srr" style="width: 150px; background-color: lightyellow;">VENDOR</th>
                            <th class="srr" style="width: 120px; background-color: lightyellow;">CATEGORY</th>
                            
                            <th class="srr" style="width: 80px; background-color: lightblue;">IN STOCK</th>
                            <th class="srr" style="width: 80px; background-color: lightblue;">RESERVED</th>
                            <th class="srr" style="width: 80px; background-color: lightblue;">AVAILABLE</th>
                            <th class="srr" style="width: 120px; background-color: lightblue;">STATUS</th>
                            
                            <th class="srr" style="width: 120px; background-color: lightgreen;">LANDED COST</th>
                            <th class="srr" style="width: 120px; background-color: lightgreen;">SELLING PRICE</th>
                            <th class="srr" style="width: 120px; background-color: lightgreen;">MARGIN</th>
                            
                            <th class="srr" style="width: 200px; background-color: #ffebee;">LOCATION</th>
                            <th class="srr" style="width: 80px; background-color: #ffebee;">CUBES</th>
                            <th class="srr" style="width: 100px; background-color: #ffebee;">QTY/BOX</th>
                            
                            <th class="srr" style="width: 100px; background-color: #fff3e0;">PO STATUS</th>
                            <!-- <th class="srr" style="width: 150px; background-color: #fff3e0;">LAST UPDATED</th>
                            <th class="srr" style="width: 100px; background-color: #fff3e0;">ACTION</th> -->
                        </tr>
                    </thead>

                    <tbody>
                        <tr v-for="item,idx in paginatedItems" v-bind:key="idx" 
                            :style="{ backgroundColor: getRowColor(item) }"
                            :class="{ 'has-active-po': item.has_active_po }">
                            <td class="srrv" style="text-align: center;">
                                <input
                                    type="checkbox"
                                    v-model="selectedItems"
                                    :value="item"
                                    :disabled="item.has_active_po"
                                    :title="item.has_active_po ? 'Active PO exists - cannot select' : ''"
                                >
                                <div v-if="item.has_active_po" style="font-size: 10px; color: #dc3545;">
                                    Active PO
                                </div>
                            </td>
                            <td class="srrv" style="font-weight: bold;">{{item.product_sku}}</td>
                            <td class="srrv">{{item.product_name}}</td>
                            <td class="srrv">{{item.vendor}}</td>
                            <td class="srrv">{{item.category}}</td>
                            
                            <td class="srrv" :class="{'text-danger': item.in_stock <= 2}">
                                {{item.in_stock}}
                            </td>
                            <td class="srrv">{{item.reserved}}</td>
                            <td class="srrv" :class="getAvailableClass(item)">
                                {{item.available}}
                            </td>
                            <td class="srrv">
                                <b-badge :variant="getStatusVariant(item)">
                                    {{ getStatusText(item) }}
                                </b-badge>
                            </td>
                            
                            <td class="srrv">${{ parseFloat(item.landed_cost).toFixed(2) }}</td>
                            <td class="srrv">${{ parseFloat(item.selling_price).toFixed(2) }}</td>
                            <td class="srrv" :class="getMarginClass(item)">
                                {{ calculateMargin(item) }}%
                            </td>
                            
                            <td class="srrv">
                                <span v-if="item.locsub">
                                    {{ item.location_name }} ({{ item.sublocation_name }})
                                </span>
                            </td>
                            <td class="srrv">{{item.cubes}}</td>
                            <td class="srrv">{{item.quantity_per_box}}</td>
                            
                            <td class="srrv">
                                <span v-if="item.has_active_po" class="po-indicator">
                                    PO: {{ item.po_count }}
                                </span>
                                <span v-else style="color: #28a745; font-size: 12px;">
                                    No Active PO
                                </span>
                            </td>
                            
                            <!-- <td class="srrv">{{ formatDate(item.last_updated) }}</td>
                            <td class="srrv">
                                <div style="display: flex; gap: 5px;">
                                    <b-button 
                                        size="sm" 
                                        variant="outline-info"
                                        @click="toggleDetails(item)"
                                        v-b-tooltip.hover title="View Details"
                                    >
                                        <b-icon :icon="item.show_details ? 'chevron-up' : 'chevron-down'"></b-icon>
                                    </b-button>
                                    
                                    <b-button 
                                        size="sm" 
                                        variant="outline-warning"
                                        @click="editItem(item)"
                                        v-b-tooltip.hover title="Edit"
                                    >
                                        <b-icon icon="pencil"></b-icon>
                                    </b-button>
                                </div>
                            </td> -->
                        </tr>
                        
                        <!-- Details Row -->
                        <tr v-for="item,idx in paginatedItems" v-bind:key="'detail-' + idx" 
                            v-if="item.show_details">
                            <td colspan="17" class="detail-row" style="background: #f8f9fa;">
                                <b-card no-body>
                                    <b-card-body>
                                        <div class="row">
                                            <div class="col-md-6">
                                                <h6>Product Details</h6>
                                                <table class="table table-sm">
                                                    <tbody>
                                                        <tr>
                                                            <td><strong>SKU:</strong></td>
                                                            <td>{{ item.product_sku }}</td>
                                                        </tr>
                                                        <tr>
                                                            <td><strong>Name:</strong></td>
                                                            <td>{{ item.product_name }}</td>
                                                        </tr>
                                                        <tr>
                                                            <td><strong>Vendor:</strong></td>
                                                            <td>{{ item.vendor }}</td>
                                                        </tr>
                                                        <tr>
                                                            <td><strong>Category:</strong></td>
                                                            <td>{{ item.category }}</td>
                                                        </tr>
                                                        <tr>
                                                            <td><strong>Cubes:</strong></td>
                                                            <td>{{ item.cubes }}</td>
                                                        </tr>
                                                        <tr>
                                                            <td><strong>PO Status:</strong></td>
                                                            <td>
                                                                <span v-if="item.has_active_po" class="po-indicator">
                                                                    Active PO ({{ item.po_count }})
                                                                </span>
                                                                <span v-else style="color: #28a745;">
                                                                    No Active PO
                                                                </span>
                                                            </td>
                                                        </tr>
                                                    </tbody>
                                                </table>
                                            </div>
                                            <div class="col-md-6">
                                                <h6>Inventory Details</h6>
                                                <table class="table table-sm">
                                                    <tbody>
                                                        <tr>
                                                            <td><strong>Location:</strong></td>
                                                            <td>{{ item.location_name }} ({{ item.sublocation_name }})</td>
                                                        </tr>
                                                        <tr>
                                                            <td><strong>In Stock:</strong></td>
                                                            <td>{{ item.in_stock }}</td>
                                                        </tr>
                                                        <tr>
                                                            <td><strong>Reserved:</strong></td>
                                                            <td>{{ item.reserved }}</td>
                                                        </tr>
                                                        <tr>
                                                            <td><strong>Available:</strong></td>
                                                            <td>{{ item.available }}</td>
                                                        </tr>
                                                        <tr>
                                                            <td><strong>Qty per Box:</strong></td>
                                                            <td>{{ item.quantity_per_box }}</td>
                                                        </tr>
                                                        <tr>
                                                            <td><strong>Pending PO Qty:</strong></td>
                                                            <td>{{ item.pending_quantity }}</td>
                                                        </tr>
                                                    </tbody>
                                                </table>
                                            </div>
                                        </div>
                                        
                                        <div class="row mt-3">
                                            <div class="col-md-12">
                                                <h6>Price Details</h6>
                                                <table class="table table-sm">
                                                    <tbody>
                                                        <tr>
                                                            <td><strong>Landed Cost:</strong></td>
                                                            <td>${{ parseFloat(item.landed_cost).toFixed(2) }}</td>
                                                        </tr>
                                                        <tr>
                                                            <td><strong>Wholesale Price:</strong></td>
                                                            <td>${{ parseFloat(item.wholesale_price).toFixed(2) }}</td>
                                                        </tr>
                                                        <tr>
                                                            <td><strong>Selling Price:</strong></td>
                                                            <td>${{ parseFloat(item.selling_price).toFixed(2) }}</td>
                                                        </tr>
                                                        <tr>
                                                            <td><strong>Margin:</strong></td>
                                                            <td :class="getMarginClass(item)">
                                                                {{ calculateMargin(item) }}%
                                                            </td>
                                                        </tr>
                                                    </tbody>
                                                </table>
                                            </div>
                                        </div>
                                        
                                        <!-- PO Details Section -->
                                        <div class="row mt-3" v-if="item.po_items && item.po_items.length > 0">
                                            <div class="col-md-12">
                                                <h6>Active Purchase Orders</h6>
                                                <table class="table table-sm table-bordered">
                                                    <thead>
                                                        <tr>
                                                            <th>PO Number</th>
                                                            <th>Status</th>
                                                            <th>Ordered</th>
                                                            <th>Scheduled</th>
                                                            <th>Received</th>
                                                            <th>Pending</th>
                                                            <th>Created</th>
                                                        </tr>
                                                    </thead>
                                                    <tbody>
                                                        <tr v-for="po in item.po_items" :key="po.po_id">
                                                            <td>{{ po.po_number || 'N/A' }}</td>
                                                            <td>
                                                                <b-badge :variant="po.is_created ? 'success' : 'warning'">
                                                                    {{ po.is_created ? 'Created' : 'Draft' }}
                                                                </b-badge>
                                                            </td>
                                                            <td>{{ po.quantity_ordered }}</td>
                                                            <td>{{ po.quantity_scheduled }}</td>
                                                            <td>{{ po.quantity_received }}</td>
                                                            <td>{{ po.quantity_ordered - po.quantity_received }}</td>
                                                            <td>{{ formatDate(po.po_created_at) }}</td>
                                                        </tr>
                                                    </tbody>
                                                </table>
                                            </div>
                                        </div>
                                        
                                        <div class="row mt-3" v-if="item.properties">
                                            <div class="col-md-12">
                                                <h6>Additional Properties</h6>
                                                <pre style="font-size: 12px;">{{ JSON.stringify(item.properties, null, 2) }}</pre>
                                            </div>
                                        </div>
                                    </b-card-body>
                                </b-card>
                            </td>
                        </tr>
                    </tbody>
                </table>
            </div>

            <!-- Pagination -->
            <div v-if="filtered.length > 0" class="mt-3">
                <b-pagination
                    v-model="currentPage"
                    :total-rows="filtered.length"
                    :per-page="perPage"
                    align="center"
                ></b-pagination>
            </div>
        </div>
    </div>
</template>

<script>
import moment from 'moment'
import Requestor from '@/utilities/Requestor';
import Loading from 'vue-loading-overlay';
import 'vue-loading-overlay/dist/vue-loading.css';
import DatePicker from 'vue2-datepicker';
import 'vue2-datepicker/index.css';
import { BIconFunnelFill, BIconChevronDown, BIconChevronUp, BIconPencil } from 'bootstrap-vue'
import CreateLowInventoryPO from '@/components/VendorOrders/CreateLowInventoryPO.vue';

export default {
    name: "ListLowInventory",
    props: [],
    components: { 
        Loading, 
        DatePicker,
        CreateLowInventoryPO,
        BIconFunnelFill,
        BIconChevronDown,
        BIconChevronUp,
        BIconPencil,
        EditProductForm: () => import('@/components/VendorOrders/EditProductForm.vue')
    },
    data: function(){
        return {
            packet: {},
            items: [],
            items_being_loaded: null,
            selectedItems: [],
            selectAll: false,
            
            // Filters
            vendor_filter: null,
            category_filter: null,
            search_filter: "",
            location_filter: null,
            inventory_level_filter: null,
            status_filter: null,
            po_status_filter: null, // New PO status filter
            
            // UI State
            filtersVisible: false,
            isMobile: false,
            currentPage: 1,
            perPage: 50,
            
            // Modal data
            selectedProduct: null,
            
            // Options
            inventory_level_options: [
                { value: null, text: "ALL" },
                { value: "critical", text: "Critical (≤ 2)" },
                { value: "out_of_stock", text: "Out of Stock (0)" },
                { value: "low", text: "Low (3-4)" },
            ],
            
            status_options: [
                { value: null, text: "ALL" },
                { value: "critical", text: "Critical" },
                { value: "warning", text: "Warning" },
                { value: "ok", text: "OK" },
            ],
            
            // PO Status options
            po_status_options: [
                { value: null, text: "ALL" },
                { value: "with_po", text: "With Active PO" },
                { value: "without_po", text: "Without Active PO" }
            ],
        }
    },
    
    computed: {
        filteredVendors() {
            if (!this.packet.vendors) return [{ value: null, text: "ALL" }];
            
            const vendorsWithItems = [...new Set(this.items.map(item => item.vendor_id))];
            const filtered = this.packet.vendors.filter(vendor =>
                vendorsWithItems.includes(vendor.value) || vendor.value === null
            );
            
            return [{ value: null, text: "ALL" }, ...filtered.filter(v => v.value !== null)];
        },
        
        category_options() {
            if (!this.items || this.items.length === 0) return [{ value: null, text: "ALL" }];
            
            const categories = [...new Set(this.items.map(item => item.category))].filter(Boolean);
            const options = categories.map(cat => ({ value: cat, text: cat }));
            
            return [{ value: null, text: "ALL" }, ...options];
        },
        
        location_options() {
            if (!this.packet.locations) return [{ value: null, text: "ALL" }];
            
            const locationsWithItems = [...new Set(this.items.map(item => item.location_id))];
            const filtered = this.packet.locations.filter(location =>
                locationsWithItems.includes(location.value) || location.value === null
            );
            
            return [{ value: null, text: "ALL" }, ...filtered.filter(l => l.value !== null)];
        },
        
        // Filter items: by default exclude items with active POs
        filtered() {
            let that = this;
            
            if (!this.items || this.items.length === 0) return [];
            
            // Start with all items
            let filteredItems = [...this.items];
            
            return filteredItems.filter(function(item){
                // Apply filters
                return (
                    (that.vendor_filter ? item.vendor_id == that.vendor_filter : true) &&
                    (that.category_filter ? item.category == that.category_filter : true) &&
                    (that.location_filter ? item.location_id == that.location_filter : true) &&
                    (that.search_filter ? 
                        (item.product_sku.toLowerCase().includes(that.search_filter.toLowerCase()) ||
                         item.product_name.toLowerCase().includes(that.search_filter.toLowerCase()) ||
                         item.vendor.toLowerCase().includes(that.search_filter.toLowerCase()))
                        : true
                    ) &&
                    (that.inventory_level_filter === "critical" ? item.available <= 2 : true) &&
                    (that.inventory_level_filter === "out_of_stock" ? item.available <= 0 : true) &&
                    (that.inventory_level_filter === "low" ? item.available >= 3 && item.available <= 4 : true) &&
                    (that.status_filter === "critical" ? item.available <= 2 : true) &&
                    (that.status_filter === "warning" ? item.available >= 3 && item.available <= 4 : true) &&
                    (that.status_filter === "ok" ? item.available >= 5 : true) &&
                    // Apply PO status filter
                    (that.po_status_filter === "with_po" ? item.has_active_po : 
                     that.po_status_filter === "without_po" ? !item.has_active_po : 
                     // Default: show items without active PO
                     !item.has_active_po)
                );
            });
        },
        
        // Separate computed properties for PO status
        itemsWithoutActivePO() {
            return this.filtered.filter(item => !item.has_active_po);
        },
        
        itemsWithActivePO() {
            return this.filtered.filter(item => item.has_active_po);
        },
        
        // Count of selectable items (without active PO)
        selectableItemsCount() {
            return this.selectedItems.filter(item => !item.has_active_po).length;
        },
        
        criticalCount() {
            return this.filtered.filter(item => item.available <= 2).length;
        },
        
        outOfStockCount() {
            return this.filtered.filter(item => item.available <= 0).length;
        },
        
        lowCount() {
            return this.filtered.filter(item => item.available >= 3 && item.available <= 4).length;
        },
        
        totalValue() {
            return this.filtered.reduce((total, item) => {
                return total + (item.landed_cost * item.in_stock);
            }, 0);
        },
        
        paginatedItems() {
            const start = (this.currentPage - 1) * this.perPage;
            const end = start + this.perPage;
            return this.filtered.slice(start, end);
        },
        
        showCreateLowInventoryPOButton() {
            return this.selectableItemsCount > 0;
        }
    },
    
    mounted() {
        this.handleResize();
        window.addEventListener('resize', this.handleResize);
    },
    
    beforeDestroy() {
        window.removeEventListener('resize', this.handleResize);
    },
    
    methods: {
        handleResize() {
            this.isMobile = window.innerWidth < 768;
        },
        
        onVendorChange(vendorId) {
            this.selectedItems = [];
            this.selectAll = false;
        },
        
        onLocationChange(locationId) {
            this.selectedItems = [];
            this.selectAll = false;
        },
        
        getAvailableClass(item) {
            if (item.available <= 0) return 'text-danger font-weight-bold';
            if (item.available <= 2) return 'text-warning font-weight-bold';
            return 'text-success';
        },
        
        getMarginClass(item) {
            const margin = this.calculateMargin(item);
            if (margin >= 40) return 'text-success font-weight-bold';
            if (margin >= 20) return 'text-warning';
            return 'text-danger';
        },
        
        getRowColor(item) {
            if (item.available <= 0) return '#ffebee'; // Light red
            if (item.available <= 2) return '#fff3e0'; // Light orange
            if (item.has_active_po) return '#f8f9fa'; // Light gray for items with active PO
            return '#ffffff';
        },
        
        getStatusVariant(item) {
            if (item.available <= 0) return 'danger';
            if (item.available <= 2) return 'warning';
            return 'success';
        },
        
        getStatusText(item) {
            if (item.available <= 0) return 'OUT OF STOCK';
            if (item.available <= 2) return 'CRITICAL';
            if (item.available <= 4) return 'LOW';
            return 'OK';
        },
        
        calculateMargin(item) {
            if (!item.landed_cost || item.landed_cost <= 0) return 0;
            const margin = ((item.selling_price - item.landed_cost) / item.landed_cost) * 100;
            return Math.round(margin);
        },
        
        formatDate(dateStr) {
            if (!dateStr) return "";
            return moment(dateStr).format('MM-DD-YYYY HH:mm');
        },
        
        toggleSelectAll() {
            if (this.selectAll) {
                // Only select items without active POs
                this.selectedItems = this.filtered.filter(item => !item.has_active_po);
            } else {
                this.selectedItems = [];
            }
        },
        
        toggleDetails(item) {
            item.show_details = !item.show_details;
            this.$forceUpdate();
        },
        
        editItem(item) {
            this.selectedProduct = { ...item };
            this.$bvModal.show('edit-item-modal');
        },
        
        showCreateLowInventoryPOModal() {
            if (this.selectableItemsCount === 0) {
                this.$bvToast.toast("Please select items first", {
                    title: "No Items Selected",
                    variant: "warning",
                    autoHideDelay: 1500
                });
                return;
            }
            
            // Check if any selected item has active PO (should not happen due to disabled checkboxes)
            const itemsWithActivePO = this.selectedItems.filter(item => item.has_active_po);
            if (itemsWithActivePO.length > 0) {
                this.$bvToast.toast("Some selected items have active POs and cannot be reordered", {
                    title: "Active POs Found",
                    variant: "danger",
                    autoHideDelay: 2000
                });
                // Remove items with active POs from selection
                this.selectedItems = this.selectedItems.filter(item => !item.has_active_po);
                return;
            }
            
            // Check if items from multiple vendors are selected
            const vendorIds = [...new Set(this.selectedItems.map(item => item.vendor_id))];
            if (vendorIds.length > 1) {
                this.$bvToast.toast("Please select items from the same vendor", {
                    title: "Multiple Vendors",
                    variant: "warning",
                    autoHideDelay: 1500
                });
                return;
            }
            
            this.$bvModal.show('create-low-inventory-po-modal');
            
            // Initialize the PO form in the child component
            this.$nextTick(() => {
                if (this.$refs.createLowInventoryPO && this.$refs.createLowInventoryPO.initializeForm) {
                    this.$refs.createLowInventoryPO.initializeForm();
                }
            });
        },
        
        exportToExcel() {
            const exportData = this.filtered.map(item => ({
                'SKU': item.product_sku,
                'Product Name': item.product_name,
                'Vendor': item.vendor,
                'Category': item.category,
                'In Stock': item.in_stock,
                'Reserved': item.reserved,
                'Available': item.available,
                'Status': this.getStatusText(item),
                'PO Status': item.has_active_po ? 'Active PO' : 'No Active PO',
                'PO Count': item.po_count,
                'Pending PO Quantity': item.pending_quantity,
                'Landed Cost': item.landed_cost,
                'Selling Price': item.selling_price,
                'Margin': `${this.calculateMargin(item)}%`,
                'Location': item.location_name,
                'Cubes': item.cubes,
                'Qty per Box': item.quantity_per_box,
                'Last Updated': this.formatDate(item.last_updated)
            }));
            
            const csvContent = this.convertToCSV(exportData);
            this.downloadCSV(csvContent, 'low_inventory_report.csv');
        },
        
        convertToCSV(data) {
            const headers = Object.keys(data[0]);
            const csv = [
                headers.join(','),
                ...data.map(row => headers.map(header => JSON.stringify(row[header])).join(','))
            ].join('\n');
            return csv;
        },
        
        downloadCSV(content, filename) {
            const blob = new Blob([content], { type: 'text/csv' });
            const link = document.createElement('a');
            link.href = URL.createObjectURL(blob);
            link.download = filename;
            link.click();
            URL.revokeObjectURL(link.href);
        },
        
        refreshData() {
            this.fetch_low_inventory_orders();
        },
        
        handleLowInventoryPOCreated() {
            this.$bvModal.hide('create-low-inventory-po-modal');
            this.selectedItems = [];
            this.selectAll = false;
            this.fetch_low_inventory_orders();
            
            this.$bvToast.toast("Low Inventory PO created successfully!", {
                title: "Success",
                variant: "success",
                autoHideDelay: 2000
            });
        },
        
        handleProductSaved() {
            this.$bvModal.hide('edit-item-modal');
            this.fetch_low_inventory_orders();
        },
        
        resetLowInventoryPOForm() {
            // Reset selections when modal is closed
            this.selectedItems = [];
            this.selectAll = false;
        },
        
        fetch_low_inventory_orders: function() {
            let payload = {
                userRole: this.packet.userDetails.userrole === "admin" ? "admin" : "user"
            };
            
            // Add location_id for non-admin users
            if (this.packet.userDetails.userrole !== "admin" && this.packet.userDetails.properties.location_id) {
                payload.location_id = this.packet.userDetails.properties.location_id;
            }
            
            let that = this;
            let path = "/api/sales/get_low_inventory_orders/";
            
            let s = function(response) {
                if (response.data.result){
                    that.items = response.data.result.map(item => ({
                        ...item,
                        show_details: false
                    }));
                    
                    // Set default filter to show only items without active POs
                    that.po_status_filter = "without_po";
                }
                else {
                    that.$bvToast.toast(
                        response.data.error,
                        {title: "Error", autoHideDelay: 1500, variant:"warning"}
                    );
                }
                that.items_being_loaded = false;
            }
            
            let f = function(error) {
                console.log(error);
                that.$bvToast.toast(
                    "Error occurred. Please try again.",
                    {title: "Error", autoHideDelay: 1500, variant:"danger"}
                );
                that.items_being_loaded = false;
            }
            
            this.items_being_loaded = true;
            Requestor.postAsyncP(path, payload, this, s, f);
        },
    },
    
    watch: {
        selectedItems(newVal) {
            // Only count items without active PO for selectAll
            const selectableItems = newVal.filter(item => !item.has_active_po);
            this.selectAll = selectableItems.length === this.itemsWithoutActivePO.length;
        },
        
        search_filter() {
            this.currentPage = 1;
        },
        
        vendor_filter() {
            this.currentPage = 1;
            this.selectedItems = [];
            this.selectAll = false;
        },
        
        category_filter() {
            this.currentPage = 1;
        },
        
        location_filter() {
            this.currentPage = 1;
            this.selectedItems = [];
            this.selectAll = false;
        },
        
        inventory_level_filter() {
            this.currentPage = 1;
        },
        
        status_filter() {
            this.currentPage = 1;
        },
        
        po_status_filter() {
            this.currentPage = 1;
            this.selectedItems = [];
            this.selectAll = false;
        }
    },
    
    created: function() {
        this.packet = JSON.parse(localStorage.packet);
        this.packet.userDetails = JSON.parse(localStorage.userDetails);
        this.fetch_low_inventory_orders();
    }
}
</script>

<style scoped>
.diver {
    overflow: auto;
    width: 100%;
    height: 700px;
}

td, th {
    border: 1px solid #dee2e6;
    padding: 8px;
}

table {
    table-layout: fixed;
    width: 100%;
    border-collapse: separate;
    border-spacing: 0;
}

.srr {
    font-size: 12px;
    text-align: center;
    background: #efefef;
    border: 1px solid #010101;
}

.srrv {
    font-size: 13px;
    text-align: center;
    background: white;
    border: 1px solid #010101;
    padding: 5px;
}

.detail-row {
    border-top: 2px solid #007bff;
}

/* Style for disabled checkboxes */
input[type="checkbox"]:disabled {
    opacity: 0.5;
    cursor: not-allowed;
}

/* Style for rows with active PO */
tr.has-active-po td {
    background-color: #f8f9fa !important;
    opacity: 0.8;
}

/* PO indicator badge */
.po-indicator {
    display: inline-block;
    background-color: #dc3545;
    color: white;
    font-size: 10px;
    padding: 2px 5px;
    border-radius: 3px;
}

/* Table headers - sticky top */
thead tr th {
    position: sticky;
    top: 0;
    z-index: 100;
    background-color: lightyellow;
    box-shadow: 0 2px 2px -1px rgba(0, 0, 0, 0.1);
}

/* First column (checkbox) - sticky left */
thead tr th:first-child,
tbody tr td:first-child {
    position: sticky;
    left: 0;
    z-index: 101;
    background-color: white;
    min-width: 50px;
    max-width: 50px;
    width: 50px;
    box-shadow: 2px 0 2px -1px rgba(0, 0, 0, 0.1);
}

/* SKU column - sticky left */
thead tr th:nth-child(2),
tbody tr td:nth-child(2) {
    position: sticky;
    left: 50px; /* Width of first column */
    z-index: 101;
    background-color: white;
    min-width: 150px;
    max-width: 150px;
    width: 150px;
    box-shadow: 2px 0 2px -1px rgba(0, 0, 0, 0.1);
}

/* Special handling for the intersection of top-sticky and left-sticky */
thead tr th:first-child {
    z-index: 200; /* Highest priority - top-left corner */
    left: 0;
    background-color: lightyellow !important;
}

thead tr th:nth-child(2) {
    z-index: 200; /* Highest priority - second column header */
    left: 50px;
    background-color: lightyellow !important;
}

/* Background colors for different column groups */
thead tr th:nth-child(1),
tbody tr td:nth-child(1) {
    background-color: lightyellow;
}

thead tr th:nth-child(2),
tbody tr td:nth-child(2) {
    background-color: lightyellow;
}

thead tr th:nth-child(3),
tbody tr td:nth-child(3) {
    background-color: lightyellow;
}

thead tr th:nth-child(4),
tbody tr td:nth-child(4) {
    background-color: lightyellow;
}

thead tr th:nth-child(5),
tbody tr td:nth-child(5) {
    background-color: lightyellow;
}

thead tr th:nth-child(6),
tbody tr td:nth-child(6) {
    background-color: lightblue;
}

thead tr th:nth-child(7),
tbody tr td:nth-child(7) {
    background-color: lightblue;
}

thead tr th:nth-child(8),
tbody tr td:nth-child(8) {
    background-color: lightblue;
}

thead tr th:nth-child(9),
tbody tr td:nth-child(9) {
    background-color: lightblue;
}

thead tr th:nth-child(10),
tbody tr td:nth-child(10) {
    background-color: lightgreen;
}

thead tr th:nth-child(11),
tbody tr td:nth-child(11) {
    background-color: lightgreen;
}

thead tr th:nth-child(12),
tbody tr td:nth-child(12) {
    background-color: lightgreen;
}

thead tr th:nth-child(13),
tbody tr td:nth-child(13) {
    background-color: #ffebee;
}

thead tr th:nth-child(14),
tbody tr td:nth-child(14) {
    background-color: #ffebee;
}

thead tr th:nth-child(15),
tbody tr td:nth-child(15) {
    background-color: #ffebee;
}

thead tr th:nth-child(16),
tbody tr td:nth-child(16) {
    background-color: #fff3e0;
}

/* Hover effects */
tbody tr:hover td {
    background-color: rgba(0, 123, 255, 0.05) !important;
}

/* For mobile responsiveness */
@media (max-width: 768px) {
    .diver {
        height: auto;
        overflow-x: auto;
        overflow-y: auto;
    }
    
    /* Disable sticky positioning on mobile */
    thead tr th,
    tbody tr td,
    thead tr th:first-child,
    tbody tr td:first-child,
    thead tr th:nth-child(2),
    tbody tr td:nth-child(2) {
        position: static !important;
        left: auto !important;
        z-index: auto !important;
        min-width: auto !important;
        max-width: auto !important;
        width: auto !important;
        box-shadow: none !important;
    }
    
    /* Adjust column widths for mobile */
    table {
        table-layout: auto;
    }
}

/* Ensure the table cells maintain their styling */
tbody tr td.srrv {
    background-color: white;
}

/* For selected rows */
tbody tr.selected td {
    background-color: #e3f2fd !important;
}

/* For detail rows */
.detail-row td {
    background-color: #f8f9fa !important;
}
</style>


// WEBPACK FOOTER //
// src/components/VendorOrders/ListLowInventory.vue